import { siteConfig } from './siteConfig';

// Where the forms send. Configurable, in order of precedence:
//   1. window.__ENV__.API_BASE_URL - runtime, from /env.js (container env API_BASE_URL), no rebuild
//   2. VITE_API_BASE_URL           - build time, from apps/website/.env
//   3. http://localhost:5001       - local dev server only
// A production build with neither set has no API address: the forms say so and send nothing,
// rather than posting to a visitor's localhost.
const runtimeApiBase = typeof window !== 'undefined' ? window.__ENV__?.API_BASE_URL : undefined;
export const API_BASE_URL = (
  runtimeApiBase ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  (import.meta.env.DEV ? 'http://localhost:5001' : '')
).replace(/\/+$/, '');

function endpoint(path: string): string {
  if (!API_BASE_URL) {
    throw new ApiError(
      `This form isn't connected yet. Email ${siteConfig.contact.general} and we'll reply within one business day.`,
    );
  }
  return `${API_BASE_URL}${path}`;
}

// Types for form data
export interface DemoFormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone?: string;
  jobTitle: string;
  country: string;
  interest?: string;
  demoDate?: string;
  additionalInfo?: string;
}

export interface SupportFormData {
  name: string;
  email: string;
  company: string;
  phone?: string;
  category: string;   // backend enum: technical | billing | general | feature_request | bug_report
  priority: string;   // backend enum: low | medium | high | urgent
  subject: string;
  description: string;
}

export interface ApiValidationError {
  type?: string;
  value?: any;
  msg: string;
  path?: string;
  param?: string;
  location?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  errors?: ApiValidationError[];
  data?: T;
}

export class ApiError extends Error {
  errors?: ApiValidationError[];
  status?: number;

  constructor(message: string, errors?: ApiValidationError[], status?: number) {
    super(message);
    this.name = 'ApiError';
    this.errors = errors;
    this.status = status;
  }
}

// Submit demo request to backend
export async function submitDemoRequest(data: DemoFormData): Promise<ApiResponse> {
  // Transform frontend form data to match backend API expectations
  const transformedData = {
    firstName: data.firstName,
    lastName: data.lastName,
    company: data.company,
    email: data.email,
    phone: data.phone?.trim() ? data.phone.trim() : undefined,
    jobTitle: data.jobTitle,
    timezone: data.country, // Map country/timezone field
    interestedProducts: data.interest ? [data.interest] : [], // Convert single interest to array
    preferredTime: data.demoDate || '', // User's preferred date/time (free-form text)
    message: data.additionalInfo || '', // Additional notes/questions
  };

  const response = await fetch(endpoint('/api/demo'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(transformedData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    let errorMessage = errorData.message || 'Failed to submit demo request';
    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      const messages = errorData.errors
        .map((e: ApiValidationError) => e.msg)
        .filter(Boolean);
      if (messages.length > 0) {
        errorMessage = messages.join('. ');
      }
    }
    throw new ApiError(errorMessage, errorData.errors, response.status);
  }

  return response.json();
}

// Submit support ticket to backend
export async function submitSupportTicket(data: SupportFormData): Promise<ApiResponse> {
  const response = await fetch(endpoint('/api/support'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    let errorMessage = errorData.message || 'Failed to submit support ticket';
    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      const messages = errorData.errors
        .map((e: ApiValidationError) => e.msg)
        .filter(Boolean);
      if (messages.length > 0) {
        errorMessage = messages.join('. ');
      }
    }
    throw new ApiError(errorMessage, errorData.errors, response.status);
  }

  return response.json();
}

